import React, { useState } from "react";

/**
 * SafeImage — premium fallback for any cover image.
 * - Renders a navy/gold gradient placeholder while loading or on error.
 * - Adds native lazy-loading + decoding hints.
 * - Keeps the same DOM/style API as <img> so it's a drop-in replacement.
 */
export default function SafeImage({
  src,
  alt = "",
  className = "",
  style = {},
  fallbackLabel = "GAI",
  loading = "lazy",
  draggable = false,
  testid,
  ...rest
}) {
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Resolve URL:
  //  - absolute (http/https) → as-is
  //  - /api/*  → REACT_APP_BACKEND_URL prefix (backend endpoint)
  //  - other /*  → same-origin (served alongside the SPA on any environment)
  const resolvedSrc = React.useMemo(() => {
    if (!src) return null;
    if (typeof src !== "string") return src;
    if (/^https?:\/\//i.test(src)) return src;
    if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
    if (src.startsWith("/")) {
      if (typeof window !== "undefined") return `${window.location.origin}${src}`;
      return src;
    }
    return src;
  }, [src]);

  const showPlaceholder = !resolvedSrc || errored;

  if (showPlaceholder) {
    return (
      <div
        className={className}
        style={{
          ...style,
          background:
            "linear-gradient(135deg, var(--gai-grey-100) 0%, var(--gai-grey-50) 50%, var(--gai-grey-200) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
        data-testid={testid}
        {...rest}
      >
        {/* Decorative grain */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.35,
            background:
              "radial-gradient(circle at 30% 20%, rgba(227,30,36,0.10), transparent 55%), radial-gradient(circle at 80% 85%, rgba(0,0,0,0.08), transparent 60%)",
          }}
        />
        <div
          className="serif"
          style={{
            color: "var(--gai-red)",
            letterSpacing: "0.32em",
            fontSize: "0.85rem",
            fontWeight: 700,
            position: "relative",
            textTransform: "uppercase",
            fontFamily: "'Playfair Display', serif",
          }}
        >
          {fallbackLabel}
        </div>
      </div>
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      loading={loading}
      decoding="async"
      draggable={draggable}
      onLoad={() => setLoaded(true)}
      onError={() => setErrored(true)}
      className={className}
      style={{
        ...style,
        background: "var(--gai-grey-100)",
        opacity: loaded ? 1 : 0,
        transition: "opacity 0.3s ease",
      }}
      data-testid={testid}
      {...rest}
    />
  );
}
